/**
 * LoadingSkeleton.jsx
 * -------------------
 * Diplomexa Institutional Luxury loading patterns — replaces the
 * twelve ad-hoc "Loading…" lines scattered across the app with one
 * cohesive component family that uses the brand gold shimmer wash.
 *
 * Usage:
 *   <LoadingSkeleton variant="card" />        – single luxury card placeholder
 *   <LoadingSkeleton variant="cards" count={3} /> – grid of cards
 *   <LoadingSkeleton variant="lines" count={5} />  – text-row placeholders
 *   <LoadingSkeleton variant="table" rows={6} />   – table rows
 *   <LoadingSkeleton variant="hero" />              – marketing hero placeholder
 *   <LoadingSkeleton variant="pass" />              – Access Pass card placeholder
 *   <LoadingSkeleton variant="page" />              – full-page veil with eyebrow + headline
 *
 * All variants pulse with a soft gold-tinted shimmer (defined in
 * index.css → `.shimmer`).  No SVGs, no images, no animation libraries
 * — pure CSS for zero bundle cost.
 */
import { Loader2 } from "lucide-react";


export default function LoadingSkeleton({
  variant = "lines",
  count = 3,
  rows = 5,
  label,
  className = "",
  testId = "loading-skeleton",
}) {
  switch (variant) {
    case "page":
      return <PageVeil label={label} testId={testId} />;
    case "hero":
      return <HeroSkeleton testId={testId} />;
    case "pass":
      return <PassSkeleton testId={testId} />;
    case "card":
      return <CardSkeleton testId={testId} />;
    case "cards":
      return (
        <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`} data-testid={testId}>
          {Array.from({ length: count }).map((_, i) => (
            <CardSkeleton key={i} testId={`${testId}-${i}`} />
          ))}
        </div>
      );
    case "table":
      return <TableSkeleton rows={rows} testId={testId} className={className} />;
    case "lines":
    default:
      return <LinesSkeleton count={count} testId={testId} className={className} />;
  }
}


function Pulse({ className = "", style }) {
  return (
    <div
      className={`relative overflow-hidden bg-slate-200 ${className}`}
      style={style}
      aria-hidden
    >
      <span className="shimmer absolute inset-0" />
    </div>
  );
}


function LinesSkeleton({ count, className, testId }) {
  return (
    <div className={`space-y-3 ${className}`} data-testid={testId}>
      {Array.from({ length: count }).map((_, i) => (
        <Pulse
          key={i}
          className="h-3 rounded-sm"
          style={{ width: `${85 - i * 9}%` }}
        />
      ))}
    </div>
  );
}


function CardSkeleton({ testId }) {
  return (
    <div
      className="rounded-sm border border-slate-200 bg-white p-5 space-y-4"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <Pulse className="h-10 w-10 rounded-sm" />
        <Pulse className="h-4 w-14 rounded-sm" />
      </div>
      <Pulse className="h-3 w-20 rounded-sm" />
      <Pulse className="h-6 w-3/4 rounded-sm" />
      <Pulse className="h-3 w-1/2 rounded-sm" />
      <div className="pt-2 border-t border-slate-100">
        <Pulse className="h-3 w-24 rounded-sm" />
      </div>
    </div>
  );
}


function TableSkeleton({ rows, className, testId }) {
  return (
    <div
      className={`rounded-sm border border-slate-200 bg-white overflow-hidden ${className}`}
      data-testid={testId}
    >
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <Pulse className="h-4 w-28 rounded-sm" />
        <Pulse className="h-4 w-16 rounded-sm" />
      </div>
      <div className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-5 py-3 grid grid-cols-4 gap-3 items-center">
            <Pulse className="h-3 w-3/4 rounded-sm" />
            <Pulse className="h-3 w-2/3 rounded-sm" />
            <Pulse className="h-3 w-1/2 rounded-sm" />
            <Pulse className="h-3 w-1/3 rounded-sm justify-self-end" />
          </div>
        ))}
      </div>
    </div>
  );
}


function HeroSkeleton({ testId }) {
  return (
    <section
      className="relative bg-brand-navy-dark overflow-hidden py-24 lg:py-32"
      data-testid={testId}
    >
      <div className="absolute inset-0 bg-noise pointer-events-none" />
      <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-brand-gold/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 space-y-6">
          <div className="h-3 w-40 bg-white/10 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
          <div className="h-14 w-3/4 bg-white/10 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
          <div className="h-14 w-2/3 bg-white/10 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
          <div className="h-4 w-1/2 bg-white/5 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
        </div>
        <div className="lg:col-span-5 hidden lg:block">
          <div className="rounded-sm bg-white/5 border border-brand-gold/20 p-7 space-y-4 backdrop-blur-md">
            <div className="h-3 w-24 bg-white/10 relative overflow-hidden rounded-sm">
              <span className="shimmer absolute inset-0" />
            </div>
            <div className="h-7 w-3/4 bg-white/10 relative overflow-hidden rounded-sm">
              <span className="shimmer absolute inset-0" />
            </div>
            <div className="h-7 w-1/2 bg-white/10 relative overflow-hidden rounded-sm">
              <span className="shimmer absolute inset-0" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function PassSkeleton({ testId }) {
  return (
    <div
      className="relative overflow-hidden rounded-sm shadow-pass-lift bg-gradient-to-br from-brand-navy-dark via-brand-navy to-brand-navy-dark text-white max-w-xl"
      data-testid={testId}
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent" />
      <div className="absolute inset-3 rounded-sm border border-brand-gold/15 pointer-events-none" />
      <div className="relative px-7 pt-7 pb-4 border-b border-brand-gold/15 space-y-4">
        <div className="h-3 w-32 bg-white/10 relative overflow-hidden rounded-sm">
          <span className="shimmer absolute inset-0" />
        </div>
        <div className="h-8 w-3/4 bg-white/10 relative overflow-hidden rounded-sm">
          <span className="shimmer absolute inset-0" />
        </div>
      </div>
      <div className="relative px-7 py-6 grid grid-cols-[auto_1fr_auto] gap-6 items-center">
        <Pulse className="h-24 w-24 rounded-sm" />
        <div className="space-y-3">
          <div className="h-3 w-20 bg-white/10 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
          <div className="h-6 w-3/4 bg-white/10 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
          <div className="h-3 w-1/2 bg-white/10 relative overflow-hidden rounded-sm">
            <span className="shimmer absolute inset-0" />
          </div>
        </div>
        <Pulse className="h-[168px] w-[168px] rounded-sm bg-white/90" />
      </div>
    </div>
  );
}


function PageVeil({ label, testId }) {
  return (
    <div
      className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center"
      data-testid={testId}
    >
      <div className="relative inline-flex items-center justify-center mb-6">
        <div className="absolute inset-0 rounded-full bg-brand-gold/15 blur-2xl animate-gold-pulse" />
        <div className="relative h-14 w-14 rounded-full bg-brand-navy-dark text-brand-gold flex items-center justify-center shadow-gold-soft">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
      <p className="eyebrow text-brand-gold">Diplomexa</p>
      <p className="mt-2 font-display text-2xl sm:text-3xl text-brand-navy-dark">
        {label || "Preparing your workspace…"}
      </p>
      <p className="mt-3 text-xs text-slate-500 uppercase tracking-[0.22em] font-semibold">
        Just a moment, securing the dais.
      </p>
    </div>
  );
}
